import { Venue } from 'types/model'
import { WidgetEvent, WidgetEventType, widgetEventTypes, widgetEvents } from './constants'

export const widgetEventManager = {
  post(
    message: { name: WidgetEvent; id?: string; data?: Record<string, any>; type?: WidgetEventType },
    options: { iframe?: HTMLIFrameElement; parent?: boolean }
  ) {
    if (options.iframe && options.iframe.contentWindow) {
      options.iframe.contentWindow.postMessage(message, '*')
    } else if (options.parent && window.parent) {
      window.parent.postMessage(message, '*')
    } else {
      window.postMessage(message, '*')
    }
  },

  listen(iframeId: string, widgetEvent: WidgetEvent, handler: (data: any) => void) {
    function listener(messageEvent: MessageEvent) {
      const messageData = messageEvent.data
      if (messageData.id !== iframeId) return
      if (messageData.name !== widgetEvent) return

      handler(messageData.data)
    }

    window.addEventListener('message', listener)
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('message', listener)
    })

    return listener
  },

  unlisten(handler: (data: any) => void) {
    window.removeEventListener('message', handler)
  },

  emitIframeResize(iframeId: string, { width, height }: { width: number; height: number }) {
    if (width && height && iframeId) {
      const message = {
        id: iframeId,
        name: widgetEvents.iframeResize,
        data: {
          iframeId,
          height,
          width,
        },
      }
      this.post(message, { parent: true })
    }
  },

  listenIframeResize(
    iframeId: string,
    handler: (data: { iframeId: string; width: number; height: number }) => void
  ) {
    this.listen(iframeId, widgetEvents.iframeResize, handler)
  },

  emitVenueDataSet(iframeId: string, venue: Venue) {
    const message = {
      id: iframeId,
      name: widgetEvents.venueDataSet,
      data: {
        iframeId,
        venue,
      },
    }
    this.post(message, { parent: true })
  },

  listenVenueDataSet(
    iframeId: string,
    handler: (data: { iframeId: string; venue: Venue }) => void
  ) {
    this.listen(iframeId, widgetEvents.venueDataSet, handler)
  },

  emitEventSelected(
    iframeId: string,
    eventId: string,
    options?: {
      attributionId?: string | null
    }
  ) {
    const message = {
      id: iframeId,
      name: widgetEvents.eventSelected,
      data: {
        iframeId,
        event: eventId,
        attributionId: options?.attributionId,
      },
    }

    this.post(message, { parent: true })
  },

  listenEventSelected(
    iframeId: string,
    handler: (data: { iframeId: string; event: string; attributionId?: string | null }) => void
  ) {
    this.listen(iframeId, widgetEvents.eventSelected, handler)
  },

  emitInventorySelected(iframeId: string, inventory: string) {
    const message = {
      id: iframeId,
      name: widgetEvents.inventorySelected,
      data: {
        iframeId,
        inventory,
      },
    }

    this.post(message, { parent: true })
  },

  listenInventorySelected(
    iframeId: string,
    handler: (data: { iframeId: string; inventory: string }) => void
  ) {
    this.listen(iframeId, widgetEvents.inventorySelected, handler)
  },

  emitCheckoutFlowStarted(iframeId: string, booking: string) {
    const message = {
      id: iframeId,
      name: widgetEvents.checkoutFlowStarted,
      data: {
        iframeId,
        booking,
      },
    }

    this.post(message, { parent: true })
  },

  listenCheckoutFlowStarted(
    iframeId: string,
    handler: (data: { iframeId: string; booking: string }) => void
  ) {
    this.listen(iframeId, widgetEvents.checkoutFlowStarted, handler)
  },

  emitStripeCheckoutRedirect(iframeId: string, stripeUrl: string) {
    const message = {
      id: iframeId,
      name: widgetEvents.stripeCheckoutRedirect,
      data: {
        iframeId,
        stripeUrl: stripeUrl,
      },
    }
    this.post(message, { parent: true })
  },

  listenStripeCheckoutRedirect(
    iframeId: string,
    handler: (data: { iframeId: string; stripeUrl: string }) => void
  ) {
    this.listen(iframeId, widgetEvents.stripeCheckoutRedirect, handler)
  },

  emitGuestlistModalOpen(iframeId: string, data: { venue: string; date: string }) {
    const { venue, date } = data
    const message = {
      id: iframeId,
      name: widgetEvents.guestlistModalOpen,
      data: {
        iframeId,
        venue,
        date,
      },
    }
    this.post(message, { parent: true })
  },

  listenGuestlistModalOpen(
    iframeId: string,
    handler: (data: { iframeId: string; venue: string; date: string }) => void
  ) {
    this.listen(iframeId, widgetEvents.guestlistModalOpen, handler)
  },

  emitReservationModalOpen(iframeId: string, data: { venue: string; date: string }) {
    const { venue, date } = data
    const message = {
      id: iframeId,
      name: widgetEvents.reservationModalOpen,
      data: {
        iframeId,
        venue,
        date,
      },
    }
    this.post(message, { parent: true })
  },

  listenReservationModalOpen(
    iframeId: string,
    handler: (data: { iframeId: string; venue: string; date: string }) => void
  ) {
    this.listen(iframeId, widgetEvents.reservationModalOpen, handler)
  },

  updateWidgetPreviewDimensions({
    width,
    height,
    iframeId,
  }: {
    width: number
    height: number
    iframeId: string
  }) {
    if (width && height && iframeId) {
      const message = {
        id: iframeId,
        iframeId,
        name: widgetEvents.tlproIframeUpdate,
        type: widgetEventTypes.dimensionsUpdated,
        data: {
          iframeId,
          name: widgetEvents.tlproIframeUpdate,
          type: widgetEventTypes.dimensionsUpdated,
          height,
          width,
        },
      }
      widgetEventManager.post(message, { parent: true })
    }
  },
}
